import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { employeesApi, workstationsApi } from '../api/endpoints';
import { Badge } from '../components/Badge';
import { Field } from '../components/Field';
import { Notice } from '../components/Notice';
import { Pagination } from '../components/Pagination';
import { WorkstationCreateModal } from '../components/WorkstationFormModal';
import { diagnosticTypeLabel } from '../domain/diagnostics';
import { eventTypeLabel } from '../domain/events';
import { canWrite } from '../domain/roles';
import {
  WORKSTATION_STATES,
  WORKSTATION_STATE_LABELS,
  workstationStateLabel,
  workstationStateTone,
  type WorkstationState,
} from '../domain/workstations';
import { useAsync } from '../hooks/useAsync';
import { formatDateTime, fullName } from '../lib/format';
import { useLiveEvents } from '../realtime/RealtimeContext';
import { useCurrentUser } from '../session/SessionContext';

export function WorkstationsPage() {
  const user = useCurrentUser();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [state, setState] = useState<WorkstationState | ''>('');
  const [owner, setOwner] = useState('');
  const [page, setPage] = useState(1);
  const [creating, setCreating] = useState(false);

  const employees = useAsync((signal) => employeesApi.list({}, signal), []);
  // La liste porte l'état et le propriétaire courants, tous deux dérivés du
  // journal : tout événement peut les faire bouger, y compris sur un poste
  // d'une autre page. La page est donc relue plutôt que rapiécée.
  const workstations = useAsync(
    (signal) =>
      workstationsApi.list(
        {
          recherche: search === '' ? undefined : search,
          etat: state === '' ? undefined : state,
          proprietaire: owner === '' ? undefined : owner,
          page,
          pageSize: 25,
        },
        signal,
      ),
    [search, state, owner, page],
  );

  useLiveEvents(() => workstations.reload());

  const resetPage = (): void => setPage(1);

  return (
    <div className="pile">
      <div className="entete-page">
        <div>
          <h1>Postes</h1>
          <p className="entete-page__sous-titre">
            Propriétaire, dernière action et dernier diagnostic sont calculés depuis le
            journal.
          </p>
        </div>
        {canWrite(user.role) && (
          <button
            type="button"
            className="bouton bouton--primaire"
            onClick={() => setCreating(true)}
          >
            Nouveau poste
          </button>
        )}
      </div>

      <div className="outils">
        <Field label="Recherche" hint="Nom, marque, modèle, numéro de série.">
          <input
            className="champ--recherche"
            value={search}
            placeholder="Rechercher un poste…"
            onChange={(event) => {
              setSearch(event.target.value);
              resetPage();
            }}
          />
        </Field>

        <Field label="État">
          <select
            value={state}
            onChange={(event) => {
              setState(event.target.value as WorkstationState | '');
              resetPage();
            }}
          >
            <option value="">Tous</option>
            {WORKSTATION_STATES.map((value) => (
              <option key={value} value={value}>
                {WORKSTATION_STATE_LABELS[value]}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Propriétaire courant">
          <select
            value={owner}
            onChange={(event) => {
              setOwner(event.target.value);
              resetPage();
            }}
          >
            <option value="">Tous</option>
            <option value="aucun">Sans propriétaire</option>
            {(employees.data?.employees ?? []).map((employee) => (
              <option key={employee.id} value={employee.id}>
                {fullName(employee)}
              </option>
            ))}
          </select>
        </Field>
      </div>

      {employees.error !== null && (
        <Notice tone="alerte" title="Liste des salariés indisponible">
          {employees.error} Le filtre par propriétaire est incomplet.
        </Notice>
      )}
      {workstations.error !== null && (
        <Notice tone="erreur" title="Chargement impossible">
          {workstations.error}
        </Notice>
      )}

      <div className="panneau">
        <div className="tableau-cadre">
          <table className="tableau">
            <thead>
              <tr>
                <th>Nom</th>
                <th>Modèle</th>
                <th>État</th>
                <th>Propriétaire courant</th>
                <th>Dernière action</th>
                <th>Dernier diagnostic</th>
              </tr>
            </thead>
            <tbody>
              {workstations.loading && workstations.data === null && (
                <tr>
                  <td colSpan={6} className="tableau__vide">
                    Chargement…
                  </td>
                </tr>
              )}
              {workstations.data?.items.length === 0 && (
                <tr>
                  <td colSpan={6} className="tableau__vide">
                    Aucun poste ne correspond à ces critères.
                  </td>
                </tr>
              )}
              {workstations.data?.items.map((workstation) => (
                /*
                  Toute la ligne ouvre la fiche, pas seulement le nom : la cible
                  est alors large et le geste ne demande pas de viser.

                  L'accès au clavier ne vient pas de la souris : la ligne porte
                  tabindex, un rôle de bouton, et répond à Entrée comme à
                  Espace — sans quoi elle serait inatteignable autrement qu'à
                  la souris. Le focus se voit, par la même règle que le survol.
                */
                <tr
                  key={workstation.id}
                  className="tableau__ligne--ouvrable"
                  role="button"
                  tabIndex={0}
                  aria-label={`Ouvrir la fiche du poste ${workstation.name}`}
                  onClick={() => navigate(`/postes/${workstation.id}`)}
                  onKeyDown={(event) => {
                    if (event.key !== 'Enter' && event.key !== ' ') return;
                    // Espace ferait défiler la page sous la ligne activée
                    event.preventDefault();
                    navigate(`/postes/${workstation.id}`);
                  }}
                >
                  <td>
                    <span className="tableau__ligne-titre">{workstation.name}</span>
                  </td>
                  <td>
                    {workstation.brand} {workstation.model}
                  </td>
                  <td>
                    <Badge tone={workstationStateTone(workstation.state)}>
                      {workstationStateLabel(workstation.state)}
                    </Badge>
                  </td>
                  <td>
                    {workstation.currentOwner === null ? (
                      <span className="faible">Aucun</span>
                    ) : (
                      <>
                        {fullName(workstation.currentOwner)}
                        <div className="faible">{workstation.currentOwner.department}</div>
                      </>
                    )}
                  </td>
                  <td className="tableau__nombre">
                    {formatDateTime(workstation.lastActionAt)}
                    {workstation.lastActionType !== null && (
                      <div className="faible">
                        {eventTypeLabel(workstation.lastActionType)}
                      </div>
                    )}
                  </td>
                  {/*
                    Information, et non lien. Ouvrir directement ce diagnostic
                    n'apporte rien, et un second lien dans une ligne entièrement
                    cliquable ferait douter de ce qui va s'ouvrir.
                  */}
                  <td className="tableau__nombre">
                    {workstation.lastDiagnosticId === null ? (
                      <span className="faible">Aucun</span>
                    ) : (
                      <>
                        {formatDateTime(workstation.lastDiagnosticAt)}
                        <div className="faible">
                          {workstation.lastDiagnosticType === null
                            ? ''
                            : diagnosticTypeLabel(workstation.lastDiagnosticType)}
                        </div>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {workstations.data !== null && (
          <Pagination
            page={workstations.data.page}
            pageSize={workstations.data.pageSize}
            total={workstations.data.total}
            onPage={setPage}
          />
        )}
      </div>

      {creating && (
        <WorkstationCreateModal
          onClose={() => setCreating(false)}
          onSaved={(saved) => {
            setCreating(false);
            navigate(`/postes/${saved.id}`);
          }}
        />
      )}
    </div>
  );
}
